import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { type Message, prepareMessage } from '@adornis/mails/server/communication.js';
import type { TemplateResult } from 'lit';
import { getInstitutionByID } from '../api/institution.js';
import { InstitutionMailLog } from '../entities/InstitutionMailLog.js';
import { globalNotification } from '../GobalNotification.js';
import { mongo } from '../server/mongo.js';
import { mailer } from './communication.js';

export async function sendInstitutionEmail(options: {
  institutionID: Maybe<string>;
  email: string;
  subject: string;
  content: TemplateResult<1>;
  attachmentPaths?: string[];
  attachments?: Message['attachments'];
  cc?: Maybe<string> | string[];
}) {
  const { institutionID, email, subject, content, attachmentPaths, attachments, cc } = options;

  const [ccInstitution, replyTo] = await (async () => {
    if (!institutionID) return [undefined, undefined];
    const institution = await getInstitutionByID(institutionID)({
      settings: { replyToMailAddress: 1 },
      ccMail: 1,
    });
    const ccInstitution = institution?.ccMail ?? undefined;
    const replyTo = institution?.settings.replyToMailAddress ?? undefined;
    return [ccInstitution, replyTo];
  })();

  const recipient = new Recipient(email, cc ?? ccInstitution, undefined, replyTo);
  const message = await prepareMessage({
    subject: subject,
    html: content,
    attachments: attachments ?? attachmentPaths?.map(path => ({ path })) ?? [],
  });

  // make it possible to use this method with string html content
  if (typeof content === 'string') message.html = content;

  try {
    const _mailer = await mailer;
    if (!_mailer) throw new Error('could not get mailer');
    await _mailer.sendMail(message, recipient);
    const logCollection = mongo().collection(InstitutionMailLog._collectionName);
    await logCollection.insertOne({
      mailTemplate: options.subject,
      timestamp: new Date(),
      institutionID,
      email,
    });
    return true;
  } catch (err) {
    const translationKey = (err as any)?.extensions?.translationKey;
    if (translationKey === 'send_mail_rejected') {
      globalNotification.next(
        'Die E-Mail konnte nicht versendet werden, da die Empfänger oder Absender-Adresse derzeit blockiert ist.',
      );
    }
    logger.warn({ email, err }, 'E-Mail Konnte nicht verschickt werden; 8373');
    return false;
  }
}
